import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Button, ButtonVariants } from '@carboledger/ui';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST, removedLinksOf } from '@/lib/utils';
import { ShipmentForm } from '../components/ShipmentForm';
import {
  useGetShipmentFormQuery,
  useUpdateIncomingShipmentMutation,
  useGetLinkedContractsForShipmentQuery,
  useUnlinkContractFromShipmentMutation,
} from '../api/incomingShipmentsApi';
import { useShipmentFormOptions } from '../hooks';

// Inbounds ▸ Incoming shipments ▸ Edit. Same form as Add.
export const EditShipmentPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();

  const {
    data,
    isLoading: isLoadingShipment,
    isError,
    // GraphQL: account-scoped, so unlike the update mutation it needs no `module`.
  } = useGetShipmentFormQuery({ id: shipmentId });
  const { options, isLoading } = useShipmentFormOptions();
  const [updateIncomingShipment, { isLoading: isSubmitting }] = useUpdateIncomingShipmentMutation();

  // The chips are their own read: `values.linkedContracts` is the save payload's shape.
  const { data: linkedContracts } = useGetLinkedContractsForShipmentQuery({ id: shipmentId });
  const [unlinkContractFromShipment] = useUnlinkContractFromShipmentMutation();

  const goToShipments = useCallback(() => {
    navigate(ROUTES.incomingShipments(module));
  }, [navigate, module]);

  /**
   * Removing a chip only marks it; Save is what unlinks. The update goes first, so a failed
   * save leaves every link intact — and Cancel leaves the shipment exactly as it was.
   */
  const handleSubmit = useCallback(
    async (values) => {
      try {
        await updateIncomingShipment({ module, id: shipmentId, values }).unwrap();
        await Promise.all(
          removedLinksOf(values).map((contractId) =>
            unlinkContractFromShipment({ module, id: shipmentId, contractId }).unwrap(),
          ),
        );
        goToShipments();
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [updateIncomingShipment, unlinkContractFromShipment, module, shipmentId, goToShipments],
  );

  if (isLoadingShipment || isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !data) {
    return (
      <EmptyState
        title={t('INCOMING_SHIPMENTS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToShipments}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ShipmentForm
        title={t('INCOMING_SHIPMENTS_EDIT_TITLE')}
        submitLabel={t('COMMON_SAVE')}
        initialValues={data.values}
        onSubmit={handleSubmit}
        onClose={goToShipments}
        isSubmitting={isSubmitting}
        options={options}
        mode="edit"
        linkedContracts={linkedContracts ?? EMPTY_LIST}
        locked={data.locked}
      />
    </>
  );
};
